import { Check } from '../../../modules/design-system';
import type { ShippingAddress } from '../../../modules/api';
import { AddressLines, DefaultBadge } from '../shared';

export interface SelectableAddressRowProps {
  address: ShippingAddress;
  selected: boolean;
  onSelect: () => void;
}

/**
 * One selectable address card in the picker (light card → dark text). The
 * whole row is the click target (web parity — radio-style, not a native
 * radio input): a green ring + filled check marks the current selection, and
 * a "Default" badge flags the player's default address.
 */
export function SelectableAddressRow({ address, selected, onSelect }: SelectableAddressRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className="flex w-full items-start justify-between gap-3 rounded-[18px] p-4 text-left transition-colors"
      style={{
        color: 'var(--co-AddressPickerModal-rowText, #15181E)',
        border: selected
          ? '1.5px solid var(--co-AddressPickerModal-rowSelectedBorder, #16B814)'
          : '1.5px solid var(--co-AddressPickerModal-rowBorder, rgba(0,0,0,0.10))',
        background: selected
          ? 'var(--co-AddressPickerModal-rowSelectedFill, rgba(34,225,28,0.07))'
          : 'var(--co-AddressPickerModal-rowFill, rgba(0,0,0,0.02))',
      }}
    >
      <div className="min-w-0">
        {address.isDefault && (
          <div className="mb-1.5">
            <DefaultBadge />
          </div>
        )}
        <AddressLines address={address} />
      </div>

      <span
        aria-hidden
        className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
        style={
          selected
            ? {
                background: 'var(--co-AddressPickerModal-rowSelectedBorder, #16B814)',
                color: '#FFFFFF',
              }
            : { border: '1.5px solid var(--co-AddressPickerModal-radioBorder, rgba(0,0,0,0.25))' }
        }
      >
        {selected && <Check size={13} strokeWidth={3} />}
      </span>
    </button>
  );
}
